import { AccordionItem, CmsButton, CmsMedia } from '@shared/models/cms.model';

/** Per-audience visibility flags shared by most dynamic-zone sections. */
export interface AudienceFlags {
    readonly enabledForExpert: boolean;
    readonly enabledForSeeker: boolean;
    readonly enabledForGuest: boolean;
}

/** Fields shared by every dynamic-zone block (id + CMS block metadata). */
export interface BlockMeta {
    readonly id: string;
    readonly blockId?: string | null;
    readonly styles?: unknown;
    readonly blockClassName?: string | null;
}

/** A `shared.widget-list-item` component. */
export interface WidgetListItem {
    readonly id: string;
    readonly itemLabel?: string | null;
    readonly itemListMarker?: CmsMedia | null;
}

/** A `shared.search-autocomplete` component. */
export interface SearchAutocomplete extends AudienceFlags {
    readonly id: string;
    readonly content?: string | null;
    readonly blockId?: string | null;
    readonly styles?: unknown;
    readonly blockClassName?: string | null;
    readonly mode: 'Extended' | 'Short';
}

/** A `shared.success-stories-item` component. */
export interface SuccessStoriesItem {
    readonly id: string;
    readonly fullName: string;
    readonly shortInfo?: string | null;
    readonly story?: string | null;
    readonly avatarImg?: CmsMedia | null;
}

/** A `button.sign-up-button` component. */
export interface SignUpButton {
    readonly id: string;
    readonly style: 'btn' | 'btn-flat';
    readonly label: string;
    readonly buttonAlignment: 'left' | 'center' | 'right';
}

/** Slider render mode: a plain carousel (`default`) or a tabbed view keyed by each item's `sliderTabName`. */
export enum SliderMode {
    Default = 'default',
    TabsView = 'tabsView',
}

/** A `shared.slider-item` component. */
export interface SliderItem {
    readonly id: string;
    readonly sliderItemContent?: string | null;
    readonly sliderImage?: CmsMedia | null;
    readonly sliderTabName?: string | null;
}

/** A `shared.card-block-item` component. */
export interface CardBlockItem {
    readonly id: string;
    readonly tagLabel?: string | null;
    readonly content?: string | null;
    readonly image?: CmsMedia | null;
    readonly buttonLink?: CmsButton | null;
}

/** A `shared.tag-item` component. */
export interface TagItem {
    readonly id: string;
    readonly tagName: string;
}

/** A `shared.tags-wizard-item` component: one wizard step (title + rich content + tag chips). */
export interface TagsWizardItem {
    readonly id: string;
    readonly wizardItemTitle?: string | null;
    readonly wizardItemContent?: string | null;
    readonly tagsList: ReadonlyArray<TagItem>;
    readonly scrollToAnchor?: string | null;
    readonly scrollToBtnLabel?: string | null;
}

/** A `shared.cards-content-group-item` component. */
export interface CardsContentGroupItem {
    readonly id: string;
    readonly title?: string | null;
    readonly content?: string | null;
    readonly backgroundColorHex?: string | null;
    readonly styles?: unknown;
    readonly itemsList: ReadonlyArray<AccordionItem>;
}

/** A `shared.how-it-works-item` component: a step's rich content and an optional image. */
export interface HowItWorksItem {
    readonly id: string;
    readonly content: string;
    readonly image?: CmsMedia | null;
}

/** Base for every dynamic-zone section; `__typename` discriminates the concrete component. */
export interface LandingSectionBase {
    readonly __typename: string;
}

/** A `section.faq-block` dynamic-zone entry (`ComponentSectionFaqBlock`). */
export interface FaqBlockSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionFaqBlock';
    readonly content?: string | null;
    readonly accordionItems: ReadonlyArray<AccordionItem>;
}

/** A `section.widget-list-block` dynamic-zone entry (`ComponentSectionWidgetListBlock`). */
export interface WidgetListBlockSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionWidgetListBlock';
    readonly imageBlockTitle?: string | null;
    readonly titleContent?: string | null;
    readonly content?: string | null;
    readonly widgetListBlockContentPosition: 'right' | 'left';
    readonly imageBlockWidthPct: number;
    readonly imageMaxWidthPx?: number | null;
    readonly imageAlignment: 'left' | 'center' | 'right';
    readonly contentBlockWidthPct: number;
    readonly contentBlockMaxWidthPx?: number | null;
    readonly contentAlignment: 'left' | 'center' | 'right';
    readonly image?: CmsMedia | null;
    readonly backgroundImage?: CmsMedia | null;
    readonly buttonLink?: CmsButton | null;
    readonly items: ReadonlyArray<WidgetListItem>;
    readonly search?: SearchAutocomplete | null;
}

/** A `section.success-stories` dynamic-zone entry (`ComponentSectionSuccessStories`). */
export interface SuccessStoriesSection extends LandingSectionBase, BlockMeta {
    readonly __typename: 'ComponentSectionSuccessStories';
    readonly content?: string | null;
    readonly storiesList: ReadonlyArray<SuccessStoriesItem>;
}

/** A `section.static-block` dynamic-zone entry (`ComponentSectionStaticBlock`). */
export interface StaticBlockSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionStaticBlock';
    readonly content?: string | null;
    readonly staticBlockContentBlockWidthPct?: number | null;
    readonly staticBlockContentBlockMaxWidthPx?: number | null;
    readonly staticBlockContentAlignment?: 'left' | 'center' | 'right' | null;
    readonly signUpButton?: SignUpButton | null;
    readonly buttonLink?: CmsButton | null;
    readonly items: ReadonlyArray<WidgetListItem>;
    readonly staticBlockBackgroundImage?: CmsMedia | null;
}

/** A `section.slider-block` dynamic-zone entry (`ComponentSectionSliderBlock`). */
export interface SliderBlockSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionSliderBlock';
    readonly titleContent?: string | null;
    readonly sliderMode?: SliderMode | null;
    readonly sliderItems: ReadonlyArray<SliderItem>;
}

/**
 * Resolves a slider block's render mode, treating a missing, `null` or unrecognised value as
 * {@link SliderMode.Default}. Use this instead of reading `sliderMode` directly so older/incomplete
 * CMS data always falls back to the plain carousel.
 */
export function resolveSliderMode(section: SliderBlockSection): SliderMode {
    return section.sliderMode === SliderMode.TabsView ? SliderMode.TabsView : SliderMode.Default;
}

/** Layout for a cards block: a horizontal grid (`row`) or intro-beside-cards split (`column`). */
export const CardsBlockPosition: { readonly Row: 'row'; readonly Column: 'column' } = {
    Row: 'row',
    Column: 'column',
};
export type CardsBlockPosition = (typeof CardsBlockPosition)[keyof typeof CardsBlockPosition];

/** A `section.cards-block` dynamic-zone entry (`ComponentSectionCardsBlock`). */
export interface CardsBlockSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionCardsBlock';
    readonly titleContent?: string | null;
    readonly position: CardsBlockPosition;
    readonly backgroundImageDesktop?: CmsMedia | null;
    readonly backgroundImageMobile?: CmsMedia | null;
    readonly buttonsList: ReadonlyArray<CmsButton>;
    readonly cardItems: ReadonlyArray<CardBlockItem>;
}

/** A `section.action-card` dynamic-zone entry (`ComponentSectionActionCard`). */
export interface ActionCardSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionActionCard';
    readonly actionCardContent?: string | null;
    readonly buttonsList: ReadonlyArray<CmsButton>;
}

/** A `section.static-block-with-tags` dynamic-zone entry (`ComponentSectionStaticBlockWithTags`). */
export interface StaticBlockWithTagsSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionStaticBlockWithTags';
    readonly staticBlockContent?: string | null;
    readonly tagsBlockContent?: string | null;
    readonly tagsList: ReadonlyArray<TagItem>;
}

/**
 * A `section.static-block-with-tags-wizard` dynamic-zone entry
 * (`ComponentSectionStaticBlockWithTagsWizard`): an intro static block followed by a repeatable list
 * of wizard steps, each carrying its own rich content and tag chips.
 */
export interface StaticBlockWithTagsWizardSection
    extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionStaticBlockWithTagsWizard';
    readonly staticBlockContent?: string | null;
    readonly wizardItems: ReadonlyArray<TagsWizardItem>;
}

/** A `section.content-group` dynamic-zone entry (`ComponentSectionContentGroup`). */
export interface ContentGroupSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionContentGroup';
    readonly contentGroupContent?: string | null;
    readonly itemsList: ReadonlyArray<AccordionItem>;
}

/** A `section.cards-content-group` dynamic-zone entry (`ComponentSectionCardsContentGroup`). */
export interface CardsContentGroupSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionCardsContentGroup';
    readonly contentGroupContent?: string | null;
    readonly groups: ReadonlyArray<CardsContentGroupItem>;
}

/** Tab layout: labels above the content (`horizontal`) or beside it (`vertical`). */
export const TabsPosition: { readonly Horizontal: 'horizontal'; readonly Vertical: 'vertical' } = {
    Horizontal: 'horizontal',
    Vertical: 'vertical',
};
export type TabsPosition = (typeof TabsPosition)[keyof typeof TabsPosition];

/** A `section.tabs-block` dynamic-zone entry (`ComponentSectionTabsBlock`). */
export interface TabsBlockSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionTabsBlock';
    readonly content?: string | null;
    readonly tabsPosition: TabsPosition;
    readonly tabsItems: ReadonlyArray<AccordionItem>;
}

/** A `section.how-it-works-block` dynamic-zone entry (`ComponentSectionHowItWorksBlock`). */
export interface HowItWorksBlockSection extends LandingSectionBase, BlockMeta, AudienceFlags {
    readonly __typename: 'ComponentSectionHowItWorksBlock';
    readonly titleContent?: string | null;
    readonly footerContent?: string | null;
    readonly items: ReadonlyArray<HowItWorksItem>;
    readonly howItWorksBackgroundImage?: ReadonlyArray<CmsMedia> | null;
}

/** An `Error` entry the dynamic zone may surface for an unresolved component. */
export interface LandingSectionError extends LandingSectionBase {
    readonly __typename: 'Error';
    readonly code?: string | null;
    readonly message?: string | null;
}

/** Discriminated union of every concrete dynamic-zone section. */
export type LandingSection =
    | FaqBlockSection
    | WidgetListBlockSection
    | SuccessStoriesSection
    | StaticBlockSection
    | SliderBlockSection
    | CardsBlockSection
    | ActionCardSection
    | StaticBlockWithTagsSection
    | StaticBlockWithTagsWizardSection
    | ContentGroupSection
    | CardsContentGroupSection
    | TabsBlockSection
    | HowItWorksBlockSection
    | LandingSectionError;

/** The `tyme-group.tyme-group-header` component. */
export interface LandingHeader {
    readonly id: string;
    readonly styles?: unknown;
    readonly logo: CmsMedia;
    readonly navLinks: ReadonlyArray<CmsButton>;
}

/** The `footer-sections.footer-links` component (`linksList` on the footer). */
export interface FooterLinks {
    readonly id: string;
    readonly content?: string | null;
    readonly enabledForExpert: boolean;
    readonly enabledForSeeker: boolean;
    readonly enabledForGuest: boolean;
    readonly styles?: unknown;
    readonly linksList: ReadonlyArray<CmsButton>;
}

/** The `footer-sections.contact-info-block` component. */
export interface ContactInfoBlock {
    readonly id: string;
    readonly content?: string | null;
    readonly enabledForExpert: boolean;
    readonly enabledForSeeker: boolean;
    readonly enabledForGuest: boolean;
    readonly styles?: unknown;
    readonly logo?: CmsMedia | null;
    readonly linksList: ReadonlyArray<CmsButton>;
}

/** A `shared.social-item` component — one social channel link in the footer's copyright block. */
export interface SocialItem {
    readonly id: string;
    readonly label?: string | null;
    readonly link: string;
    readonly image: CmsMedia;
    // Strapi's GraphQL strips the leading underscore from the `_blank`/`_self` enum, so the API
    // returns `blank`/`self` (same as `CmsButton.target`).
    readonly target: 'blank' | 'self';
    readonly customClass?: string | null;
}

/** The `footer-sections.copyrigt-block` component. */
export interface CopyrightBlock {
    readonly id: string;
    readonly content?: string | null;
    readonly enabledForExpert: boolean;
    readonly enabledForSeeker: boolean;
    readonly enabledForGuest: boolean;
    readonly styles?: unknown;
    readonly socialLinksEnabled: boolean;
    readonly socialItems: ReadonlyArray<SocialItem>;
}

/** The `tyme-group.tyme-group-footer` component. */
export interface LandingFooter {
    readonly id: string;
    readonly styles?: unknown;
    readonly linksList: ReadonlyArray<FooterLinks>;
    readonly contactInfo?: ContactInfoBlock | null;
    readonly copyrightBlock?: CopyrightBlock | null;
}

/** A `shared.meta-social` component. */
export interface MetaSocial {
    readonly id: string;
    readonly socialNetwork?: 'Facebook' | 'Twitter' | null;
    readonly title?: string | null;
    readonly description?: string | null;
    readonly image?: CmsMedia | null;
}

/** The `shared.seo` component. */
export interface LandingSeo {
    readonly id: string;
    readonly metaTitle?: string | null;
    readonly metaDescription?: string | null;
    readonly keywords?: string | null;
    readonly metaRobots?: string | null;
    readonly metaViewport?: string | null;
    readonly canonicalURL?: string | null;
    readonly structuredData?: unknown;
    readonly metaImage?: CmsMedia | null;
    readonly metaSocial: ReadonlyArray<MetaSocial>;
}

export interface TymeGroupLanding {
    readonly documentId: string;
    readonly locale: string;
    readonly Sections: ReadonlyArray<LandingSection>;
    readonly header?: LandingHeader | null;
    readonly footer?: LandingFooter | null;
    readonly seo?: LandingSeo | null;
}

export interface LandingGraphqlResponse {
    readonly data?: {
        readonly tymeGroupLanding?: TymeGroupLanding | null;
    };
}

/** A run of consecutive dynamic-zone sections that share the same `blockId`. */
export type LandingSectionGroup = ReadonlyArray<LandingSection>;

/**
 * Reads a section's `blockId`, normalising "missing" to `null` so that an absent field and an
 * explicit `null` group together. The `Error` member of the union has no `blockId`, hence the
 * `'blockId' in section` guard.
 */
function sectionBlockId(section: LandingSection): string | null {
    return 'blockId' in section ? (section.blockId ?? null) : null;
}

/**
 * Groups sections into runs of **consecutive** entries that share the same `blockId`. A change in
 * `blockId` starts a new group; equal-but-non-adjacent ids are NOT merged. Sections whose `blockId`
 * is missing or `null` are grouped under that shared "no id" key. `null`/`undefined` entries are
 * skipped — GraphQL nulls out a whole list element when a non-nullable field on it fails to resolve,
 * so a single bad section must not break the rest of the page.
 *
 * @example
 * // sections blockIds: [1, 1, 2, 1]
 * // → [[1, 1], [2], [1]]
 */
export function groupSectionsByBlockId(
    sections: ReadonlyArray<LandingSection | null | undefined>,
): ReadonlyArray<LandingSectionGroup> {
    const groups: Array<Array<LandingSection>> = [];
    let previousId: string | null | undefined;
    let started: boolean = false;

    for (const section of sections) {
        if (section == null) {
            continue;
        }

        const id: string | null = sectionBlockId(section);

        if (started && id === previousId) {
            groups[groups.length - 1].push(section);
        } else {
            groups.push([section]);
        }

        previousId = id;
        started = true;
    }

    return groups;
}
